import { createSignal, type JSX, Show } from 'solid-js';
import {
  createProject,
  type ProjectCreateInput,
  type ProjectCreateOutcome,
} from '../lib/project-create';
import { CreateProjectForm } from './CreateProjectForm';
import { CreateProjectResult } from './CreateProjectResult';

// The greenfield create guts (#1727). Container — owns the POST and hands the
// verdict to <CreateProjectResult/>. Mounted only while the dialog is open
// (<CreateProjectModal/> is the shell), which is what resets the draft and the
// verdict between opens: a result left standing would greet the next create
// with the previous one's answer.
//
// A CREATED REPO CHANGES THE LIST BEHIND THE DIALOG, so the route re-READS on
// `onCreated` rather than patching a row in from this echo — the rule every
// write on this console holds. Nothing re-reads on a refusal or on `unclear`:
// asking the server to confirm that nothing happened is the refetch
// `<StartPlanDialog/>` refuses to make. `unclear` is the deliberate exception —
// the repo MAY exist, and the list is the only honest place that answer lives.

export type CreateProjectDialogProps = {
  readonly token: string | null;
  /** Fired once the repo (or possibly-a-repo) landed — the route re-reads. */
  readonly onCreated: () => void;
  readonly onClose: () => void;
};

export function CreateProjectDialog(props: CreateProjectDialogProps): JSX.Element {
  const [pending, setPending] = createSignal(false);
  const [outcome, setOutcome] = createSignal<ProjectCreateOutcome | null>(null);

  const submit = async (input: ProjectCreateInput): Promise<void> => {
    setPending(true);
    const result = await createProject(input, props.token);
    setOutcome(result);
    setPending(false);
    if (result.kind !== 'refused') props.onCreated();
  };

  return (
    <Show
      when={outcome()}
      fallback={
        <CreateProjectForm
          pending={pending()}
          onSubmit={(input) => void submit(input)}
          onCancel={props.onClose}
        />
      }
    >
      {(result) => (
        <CreateProjectResult
          outcome={result()}
          onAgain={() => setOutcome(null)}
          onClose={props.onClose}
        />
      )}
    </Show>
  );
}
